import type { FieldErrors } from 'react-hook-form';
import * as yup from 'yup';
import type { EventFormData } from './eventFormTypes';

const optionalUrl = yup
  .string()
  .transform((v) => (v == null || String(v).trim() === '' ? undefined : v))
  .url('Informe uma URL válida (começando com http:// ou https://)')
  .notRequired();

const ticketItemSchema = yup.object().shape({
  name: yup.string().required('O nome do ingresso é obrigatório'),
  price: yup
    .number()
    .typeError('Informe um valor numérico válido para o preço')
    .min(0, 'O preço não pode ser negativo')
    .required('O preço é obrigatório'),
  quantity: yup.number().positive('A quantidade deve ser positiva').required('A quantidade é obrigatória'),
  minPerOrder: yup
    .number()
    .transform((v, orig) => (orig === '' || orig == null ? 1 : v))
    .min(1, 'Mínimo por compra: pelo menos 1')
    .max(10, 'Mínimo por compra: no máximo 10')
    .notRequired(),
  maxPerOrder: yup
    .number()
    .nullable()
    .transform((v, orig) => (orig === '' || orig == null ? null : v))
    .min(1, 'Máximo por compra: pelo menos 1')
    .max(10, 'Máximo por compra: no máximo 10')
    .notRequired()
    .test('max-gte-min', 'Máximo deve ser maior ou igual ao mínimo', function (value) {
      if (value == null) return true;
      const min = Number(this.parent.minPerOrder) || 1;
      return value >= min;
    }),
});

const ticketTypeSchema = yup.object().shape({
  name: yup.string().required('O nome do tipo de ingresso é obrigatório'),
});

export const eventFormFullSchema = yup.object().shape({
  title: yup.string().required('O título é obrigatório').min(5, 'O título deve ter pelo menos 5 caracteres'),
  slug: yup
    .string()
    .required('O slug é obrigatório')
    .matches(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Use apenas letras minúsculas, números e hífens'),
  description: yup.string().required('A descrição é obrigatória').min(20, 'A descrição deve ter pelo menos 20 caracteres'),
  startDate: yup.date().required('A data de início é obrigatória'),
  endDate: yup
    .date()
    .required('A data de término é obrigatória')
    .min(yup.ref('startDate'), 'A data de término deve ser posterior à data de início'),
  location: yup.string().required('O local é obrigatório'),
  address: yup.string().required('O endereço é obrigatório'),
  city: yup.string().required('A cidade é obrigatória'),
  state: yup.string().required('O estado é obrigatório'),
  category: yup.string().required('A categoria é obrigatória'),
  capacity: yup.number().required('A capacidade é obrigatória').positive('A capacidade deve ser positiva'),
  mapLink: optionalUrl,
  ticketCatalogMode: yup.string().notRequired(),
  isPaid: yup.boolean().notRequired(),
  tickets: yup.array().when('ticketCatalogMode', {
    is: (mode: string) => !mode || mode === 'legacy',
    then: (schema) => schema.of(ticketItemSchema).min(1, 'Deve haver pelo menos um ingresso'),
    otherwise: (schema) => schema.notRequired(),
  }),
  ticketTypes: yup.array().when('ticketCatalogMode', {
    is: (mode: string) => mode === 'phased' || mode === 'batched',
    then: (schema) => schema.of(ticketTypeSchema).min(1, 'Configure pelo menos um tipo de ingresso'),
    otherwise: (schema) => schema.notRequired(),
  }),
  salesPhases: yup.array().when('ticketCatalogMode', {
    is: 'phased',
    then: (schema) => schema.min(1, 'Configure pelo menos um lote de venda'),
    otherwise: (schema) => schema.notRequired(),
  }),
  registrationDeadline: yup
    .date()
    .required('O prazo é obrigatório')
    .test('valid-year', 'Escolha uma data válida ou use uma sugestão rápida', (value) => {
      if (!value) return false;
      return value.getFullYear() >= 2000;
    })
    .max(yup.ref('startDate'), 'O prazo deve ser anterior ao início do evento'),
  postRegistrationUrl: optionalUrl,
});

export const REGISTRATION_SETTINGS_FIELDS = [
  'registrationDeadline',
  'customFields',
  'allowWaitlist',
  'allowGroupRegistration',
  'requireApproval',
  'sendConfirmationEmail',
] as const;

export const EVENT_FORM_DEFAULT_VALUES: EventFormData = {
  title: '',
  slug: '',
  description: '',
  startDate: new Date(new Date().setHours(new Date().getHours() + 24)),
  endDate: new Date(new Date().setHours(new Date().getHours() + 28)),
  location: '',
  mapLink: '',
  address: '',
  city: '',
  state: '',
  category: 'other',
  capacity: 100,
  showRemainingSpots: false,
  isPaid: false,
  tickets: [
    {
      name: 'Ingresso Padrão',
      description: '',
      price: 0,
      quantity: 100,
      isRestricted: false,
      allowedEmails: [],
      minPerOrder: 1,
      maxPerOrder: null,
      allowSameAttendeeData: true,
      isActive: true,
      hiddenFromPublicPage: false,
    },
  ],
  ticketCatalogMode: 'legacy',
  salesPhases: [],
  ticketTypes: [],
  ticketLinks: [],
  hasStore: false,
  products: [],
  registrationDeadline: new Date(new Date().setHours(new Date().getHours() + 20)),
  customFields: [],
  coverImage: null,
  galleryImages: [],
  contactEmail: '',
  contactPhone: '',
  showMap: true,
  showSocialShare: true,
  customTheme: {
    primaryColor: '#3f51b5',
    secondaryColor: '#f50057',
    fontFamily: 'Roboto',
  },
  postRegistrationUrl: '',
  status: 'pre_published',
  allowInstallments: false,
};

export function convertEventFormDateFields(data?: Partial<EventFormData>) {
  if (!data) return {};

  return {
    ...data,
    startDate: data.startDate ? new Date(data.startDate) : null,
    endDate: data.endDate ? new Date(data.endDate) : null,
    registrationDeadline: data.registrationDeadline ? new Date(data.registrationDeadline) : null,
    tickets: data.tickets?.map((t) => ({
      ...t,
      startSaleDate: t.startSaleDate ? new Date(t.startSaleDate) : undefined,
      endSaleDate: t.endSaleDate ? new Date(t.endSaleDate) : undefined,
    })),
    ticketTypes: data.ticketTypes?.map((type) => ({
      ...type,
      batches: type.batches?.map((batch) => ({
        ...batch,
        startSaleDate: batch.startSaleDate ? new Date(batch.startSaleDate) : undefined,
        endSaleDate: batch.endSaleDate ? new Date(batch.endSaleDate) : undefined,
      })),
    })),
    salesPhases: data.salesPhases?.map((phase) => ({
      ...phase,
      startSaleDate: phase.startSaleDate ? new Date(phase.startSaleDate) : undefined,
      endSaleDate: phase.endSaleDate ? new Date(phase.endSaleDate) : undefined,
    })),
    products: data.products?.map((p) => ({
      ...p,
      startSaleDate: p.startSaleDate ? new Date(p.startSaleDate) : undefined,
      endSaleDate: p.endSaleDate ? new Date(p.endSaleDate) : undefined,
    })),
  };
}

export function buildEventFormValues(initialData?: Partial<EventFormData>): EventFormData {
  return {
    ...EVENT_FORM_DEFAULT_VALUES,
    ...initialData,
    ...convertEventFormDateFields(initialData),
  } as EventFormData;
}

export function finalizeEventFormDataForSubmit(data: EventFormData): EventFormData {
  const mode = data.ticketCatalogMode || 'legacy';
  const hasPaidTicket =
    mode === 'phased'
      ? (data.salesPhases || []).some((phase) =>
          (phase.offers || []).some((offer) => (offer.price ?? 0) > 0)
        )
      : mode === 'batched'
        ? (data.ticketTypes || []).some((type) =>
            (type.batches || []).some((batch) => (batch.price ?? 0) > 0)
          )
        : (data.tickets || []).some((ticket) => (ticket.price ?? 0) > 0);

  return {
    ...data,
    isPaid: hasPaidTicket,
    allowInstallments: false,
  };
}

export function getFirstEventFormError(errors: FieldErrors): string | null {
  const stack: unknown[] = [errors];
  while (stack.length) {
    const current = stack.pop();
    if (!current || typeof current !== 'object') continue;
    if ('message' in current && typeof (current as { message?: unknown }).message === 'string') {
      return (current as { message: string }).message;
    }
    for (const value of Object.values(current)) {
      if (value && typeof value === 'object') stack.push(value);
    }
  }
  return null;
}

export const EVENT_FORM_REGISTRATION_STEP = 2;
